import { format, isAfter, parse } from 'date-fns';

import { TimeOption, toHolidayFormat } from '@abg/kit-base';
import { HolidaysSchedule, OpeningHours, TimeFormat } from '@abg/typings';

import {
  DEFAULT_LOCALE,
  PageLocaleToLocaleMap,
} from '../../../../web-core-ui/lib/molecules/date-range-picker/locales';
import {
  DATE_FNS_TIME_FORMAT_12H,
  DATE_FNS_TIME_FORMAT_24H,
  DEFAULT_WORKING_HOURS_END,
  DEFAULT_WORKING_HOURS_START,
  MINUTES_PER_DAY,
} from '../../shared/constants';

import { InitialDraftStateT } from './types';

const getSanitizedEndTime = (endTime: string): string => {
  if (endTime === '12:00 AM') {
    return '11:59 PM';
  }
  if (endTime === '00:00') {
    return '23:59';
  }
  return endTime;
};

export const formatTimePickerSubtitleTime = (
  daySchedule: OpeningHours | HolidaysSchedule | undefined,
  timeFormat: TimeFormat,
): string => {
  if (!daySchedule) return '';

  const {
    firstStartTime,
    firstEndTime,
    secondStartTime,
    secondEndTime,
    thirdStartTime,
    thirdEndTime,
  } = daySchedule;

  const formatRange = (start: number, end: number): string => {
    const formattedStart = convertMinutesToTimeFormat(start, timeFormat);
    const formattedEnd = convertMinutesToTimeFormat(end, timeFormat);
    return `${formattedStart} - ${getSanitizedEndTime(formattedEnd)}`;
  };

  const ranges: string[] = [];

  if (firstStartTime > -1 && firstEndTime >= 0) {
    ranges.push(formatRange(firstStartTime, firstEndTime));
  }

  if (secondStartTime > -1 && secondEndTime >= 0) {
    ranges.push(formatRange(secondStartTime, secondEndTime));
  }

  if (thirdStartTime > -1 && thirdEndTime >= 0) {
    ranges.push(formatRange(thirdStartTime, thirdEndTime));
  }

  return ranges.join(', ');
};

export function convertTimeToMinutes(time: string): number {
  const currentFormat = /am|pm/.test(time.toLowerCase())
    ? DATE_FNS_TIME_FORMAT_12H
    : DATE_FNS_TIME_FORMAT_24H;

  const parsedDate = parse(time, currentFormat, new Date());

  const hours = parsedDate.getHours();
  const minutes = parsedDate.getMinutes();

  return hours * 60 + minutes;
}

export function convertMinutesToTimeFormat(
  time: number,
  timeFormat: TimeFormat,
) {
  const date = new Date();
  date.setHours(Math.floor(time / 60), time % 60, 0);

  return format(
    date,
    timeFormat === '24hr' ? DATE_FNS_TIME_FORMAT_24H : DATE_FNS_TIME_FORMAT_12H,
  );
}

export const checkTodayAsUnavailableDay = ({
  timeOptionsForToday,
  timeOptionsForTodayRelatedToPickupLocation,
  withSelectedPickupSchedule,
}: {
  timeOptionsForToday: TimeOption[];
  timeOptionsForTodayRelatedToPickupLocation: TimeOption[];
  withSelectedPickupSchedule: boolean;
}): string => {
  const todayFormattedHoliday = toHolidayFormat(new Date());

  if (withSelectedPickupSchedule) {
    if (timeOptionsForTodayRelatedToPickupLocation.length === 0) {
      return todayFormattedHoliday;
    }
    return '';
  }

  if (timeOptionsForToday.length === 0) {
    return todayFormattedHoliday;
  }

  return '';
};

export const getLocationTimeOptions = ({
  daySchedule,
  timePickerOptions,
}: {
  daySchedule: OpeningHours | HolidaysSchedule | undefined;
  timePickerOptions: TimeOption[];
}) => {
  if (!daySchedule) return [];

  function getTimeOptions(startTime: number, endTime: number) {
    return timePickerOptions.filter((timeOption) =>
      startTime > endTime
        ? timeOption.minutes >= startTime || timeOption.minutes <= endTime
        : timeOption.minutes >= startTime && timeOption.minutes <= endTime,
    );
  }

  const {
    firstStartTime,
    firstEndTime,
    secondStartTime,
    secondEndTime,
    thirdStartTime,
    thirdEndTime,
  } = daySchedule;

  let resultOptions: TimeOption[] = [];

  if (firstStartTime > -1 && firstEndTime) {
    resultOptions = getTimeOptions(firstStartTime, firstEndTime);
  }

  if (secondStartTime > -1 && secondEndTime) {
    resultOptions = [
      ...resultOptions,
      ...getTimeOptions(secondStartTime, secondEndTime),
    ];
  }
  if (thirdStartTime > -1 && thirdEndTime) {
    resultOptions = [
      ...resultOptions,
      ...getTimeOptions(thirdStartTime, thirdEndTime),
    ];
  }

  const uniqueOptions: TimeOption[] = [
    ...new Set(resultOptions.map((option) => JSON.stringify(option))),
  ].map((option) => JSON.parse(option) as TimeOption);

  // Sort starting from the first opening hour
  const anchor = daySchedule.firstStartTime;
  return uniqueOptions.sort((a, b) => {
    const rotateA = (a.minutes - anchor + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    const rotateB = (b.minutes - anchor + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return rotateA - rotateB;
  });
};

export const getSplittedLocationHolidays = (
  holidays: HolidaysSchedule[],
): [HolidaysSchedule[], string[]] => {
  return holidays.reduce(
    ([working, nonWorking], holiday) => {
      if (holiday.firstStartTime === -1) {
        nonWorking.push(holiday.date);
      } else {
        working.push(holiday);
      }
      return [working, nonWorking];
    },
    [[], []] as [HolidaysSchedule[], string[]],
  );
};

export const getSelectedDaySchedule = ({
  selectedDay,
  locationOpeningHours,
  locationWorkingHolidays,
}: {
  selectedDay: Date | undefined;
  locationOpeningHours: OpeningHours[];
  locationWorkingHolidays: HolidaysSchedule[];
}): HolidaysSchedule | OpeningHours | undefined => {
  if (!selectedDay) return;
  const selectedDayIndex = selectedDay.getDay();
  const regularSchedule = locationOpeningHours.find(
    (day) => day.dayOfWeek === selectedDayIndex,
  );
  const isClosed = regularSchedule?.firstEndTime === -1;
  if (isClosed) return regularSchedule;

  const selectedFormattedDate = format(selectedDay, 'yyyy-MM-dd');
  const holidaySchedule = locationWorkingHolidays.find(
    (holiday) => holiday.date === selectedFormattedDate,
  );

  if (holidaySchedule) return holidaySchedule;

  return regularSchedule;
};

const applyDefaultWorkingHours = (options: TimeOption[]): TimeOption[] =>
  options.filter(
    (option) =>
      option.minutes >= DEFAULT_WORKING_HOURS_START ||
      option.minutes <= DEFAULT_WORKING_HOURS_END,
  );

export const getTimeOptions = ({
  isToday,
  withSchedule,
  timeOptionsForToday,
  timeOptionsForTodayRelatedToPickupLocation,
  daySchedule,
  timePickerOptions,
  is24hrs = false,
  singleDaySelectionPickupTime = '',
}: {
  isToday: boolean;
  withSchedule: boolean;
  timeOptionsForToday?: TimeOption[];
  timeOptionsForTodayRelatedToPickupLocation?: TimeOption[];
  daySchedule?: HolidaysSchedule | OpeningHours | undefined;
  timePickerOptions: TimeOption[];
  is24hrs?: boolean;
  singleDaySelectionPickupTime?: string;
}): TimeOption[] => {
  if (isToday) {
    if (withSchedule) {
      return timeOptionsForTodayRelatedToPickupLocation || [];
    }
    return applyDefaultWorkingHours(timeOptionsForToday || []);
  }

  if (singleDaySelectionPickupTime) {
    const dropoffTimeOptionsForSingleDay = formatDropoffTimeOptionsSingleDay(
      timePickerOptions,
      singleDaySelectionPickupTime,
    );

    if (is24hrs) {
      return dropoffTimeOptionsForSingleDay;
    }

    if (!withSchedule) {
      return applyDefaultWorkingHours(dropoffTimeOptionsForSingleDay);
    }

    return getLocationTimeOptions({
      daySchedule: daySchedule!,
      timePickerOptions: dropoffTimeOptionsForSingleDay,
    });
  }

  if (withSchedule) {
    if (is24hrs) {
      return timePickerOptions;
    }
    return getLocationTimeOptions({
      daySchedule: daySchedule!,
      timePickerOptions,
    });
  }

  return applyDefaultWorkingHours(timePickerOptions);
};

export const formatDropoffTimeOptionsSingleDay = (
  pickupLocationTimeOptions: TimeOption[],
  pickupTime: string,
): TimeOption[] => {
  const pickupTimeSelected = pickupLocationTimeOptions.find(
    (option) => option.value === pickupTime,
  );

  if (!pickupTimeSelected) return pickupLocationTimeOptions;

  return pickupLocationTimeOptions.filter(
    (option) => option.minutes >= pickupTimeSelected.minutes,
  );
};

export type CalculateSelectedDaysArguments = {
  baseDaysDiff: number;
  timeFormat: TimeFormat;
  pickupTime?: string;
  dropoffTime?: string;
};

export const calculateSelectedDays = ({
  baseDaysDiff,
  timeFormat,
  pickupTime,
  dropoffTime,
}: CalculateSelectedDaysArguments) => {
  if (baseDaysDiff >= 1 && pickupTime && dropoffTime) {
    const parseTimeString = (timeString: string) => {
      return timeFormat === '12hr(am/pm)'
        ? parse(timeString, 'h:mm a', new Date())
        : parse(timeString, 'HH:mm', new Date());
    };

    const pickupTimeParsed = parseTimeString(pickupTime);
    const dropoffTimeParsed = parseTimeString(dropoffTime);

    if (isAfter(dropoffTimeParsed, pickupTimeParsed)) {
      return baseDaysDiff + 1;
    }
  }

  return Math.max(baseDaysDiff, 1);
};

export const formatDatesText = (
  dates: InitialDraftStateT['dates'],
  localeCode?: string,
): string => {
  if (!dates) return '';
  const dateFnsLocale =
    PageLocaleToLocaleMap[localeCode || ''] ||
    PageLocaleToLocaleMap[DEFAULT_LOCALE];
  return `${format(dates.from as Date, 'MMM. dd, yyyy', { locale: dateFnsLocale })} – ${format(dates.to as Date, 'MMM. dd, yyyy', { locale: dateFnsLocale })}`;
};
